// Overview of a decision run (`run.kind === 'decision'`): the question, the
// choice, the reason, whether the default was used, the tools it looked
// things up with, and the cost. A decision run has no sandbox, git, PR, diff
// or checkpoints, so the run page shows this instead of the agent overview.

import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { Run, RunEvent, WorkflowState } from '@goblic/shared'
import { workflowsApi } from '../../lib/api-client'
import { formatTime, humanize } from '../../lib/format'
import { Facts, Notice, Panel } from '../ui'

/** The workflow step a decision run belongs to (`configSnapshot.workflowStep`). */
export function decisionStepOf(
  run: Run
): { workflowKey: string; state: string } | null {
  const step = (
    run.configSnapshot as {
      workflowStep?: { workflowKey?: string; state?: string }
    } | null
  )?.workflowStep
  return step?.workflowKey && step.state
    ? { workflowKey: step.workflowKey, state: step.state }
    : null
}

interface ToolCallRow {
  key: string
  name: string
  detail: string | null
  failed: boolean
  at: string | null
}

const asText = (value: unknown): string | null =>
  value == null || value === ''
    ? null
    : typeof value === 'string'
      ? value
      : JSON.stringify(value)

/** Tool calls of the run, from either event shape (`tool_call` from the decision worker, `tool_call_started` of agent runs). */
function toolCallsOf(events: RunEvent[]): ToolCallRow[] {
  const rows: ToolCallRow[] = []
  for (const event of events) {
    const e = event as unknown as Record<string, unknown> & {
      payload?: Record<string, unknown>
    }
    const type = String(e.type)
    if (type !== 'tool_call' && type !== 'tool_call_started') continue
    const data = { ...(e.payload ?? {}), ...e }
    rows.push({
      key: String(e.id ?? rows.length),
      name:
        asText(data.toolName ?? data.tool ?? data.name) ?? 'tool',
      detail: asText(
        data.redactedParams ?? data.input ?? data.params ?? data.args
      ),
      failed: data.ok === false || data.resultStatus === 'error',
      at: typeof e.timestamp === 'string' ? e.timestamp : null,
    })
  }
  return rows
}

export function DecisionRunOverview({
  run,
  events,
  cost,
}: {
  run: Run
  events: RunEvent[]
  /** Already formatted (honors the workspace's run cost setting). */
  cost: ReactNode
}) {
  const step = decisionStepOf(run)
  const workflowsQuery = useQuery({
    queryKey: ['project', run.projectId, 'workflows'],
    queryFn: () => workflowsApi.get(run.projectId!),
    enabled: run.projectId != null && step != null,
  })
  const state: WorkflowState | undefined = workflowsQuery.data?.workflows
    .find((w) => w.key === step?.workflowKey)
    ?.states.find((s) => s.name === step?.state)
  const decision = run.decision
  const toolCalls = toolCallsOf(events)
  const optionDescription = state?.options?.find(
    (o) => o.value === decision?.choice
  )?.description

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-6">
        <Panel
          title="Decision"
          description={
            step
              ? `${humanize(step.state)}${state?.variable ? `, answer stored as ${state.variable}` : ''}`
              : undefined
          }
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-ink-3">Question</p>
              <p className="whitespace-pre-wrap text-base text-ink">
                {state?.question ??
                  (workflowsQuery.isLoading ? 'Loading…' : 'Not available.')}
              </p>
            </div>
            {decision ? (
              <>
                {decision.fallback ? (
                  <Notice tone="signal">
                    The default option was used
                    {decision.rawChoice
                      ? `: the agent answered “${decision.rawChoice}”, which isn’t one of the options`
                      : run.status === 'completed'
                        ? ' because no answer was given'
                        : `, the run ${humanize(run.status).toLowerCase()}`}
                    .
                  </Notice>
                ) : null}
                <div>
                  <p className="text-sm text-ink-3">Choice</p>
                  <p className="font-mono text-base font-medium text-ink">
                    {decision.choice}
                  </p>
                  {optionDescription ? (
                    <p className="text-sm text-ink-3">{optionDescription}</p>
                  ) : null}
                </div>
                <div>
                  <p className="text-sm text-ink-3">Reason</p>
                  <p className="whitespace-pre-wrap text-base text-ink">
                    {decision.reason || 'No reason given.'}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-base text-ink-3">
                {run.status === 'running' || run.status === 'queued'
                  ? 'Deciding…'
                  : 'No decision was recorded.'}
              </p>
            )}
          </div>
        </Panel>
        <Panel
          title="Tool calls"
          description="What it looked up before deciding."
          flush
        >
          {toolCalls.length === 0 ? (
            <p className="px-5 py-4 text-base text-ink-3">
              It decided without looking anything up.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {toolCalls.map((call) => (
                <li key={call.key} className="px-5 py-2.5 text-base">
                  <span
                    className={`font-mono ${call.failed ? 'text-danger' : 'text-ink'}`}
                  >
                    {call.name}
                  </span>
                  {call.at ? (
                    <span className="ml-2 text-sm text-ink-3">
                      {formatTime(call.at)}
                    </span>
                  ) : null}
                  {call.detail ? (
                    <p className="truncate font-mono text-sm text-ink-3" title={call.detail}>
                      {call.detail}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <div className="space-y-6">
        <Panel title="Run">
          <Facts
            items={[
              { label: 'Status', value: humanize(run.status) },
              { label: 'Model', value: run.model || '—' },
              { label: 'Cost', value: cost },
              {
                label: 'Task',
                value: (
                  <Link to="/tasks" search={{ taskId: run.taskId }} className="link">
                    Open the task
                  </Link>
                ),
              },
            ]}
          />
        </Panel>
      </div>
    </div>
  )
}
